/**
 * The By record tab (mission 6 WP08 T046; FR-013, FR-014; ANA.2, P.8; mission
 * 5 results-api.md "by-record"; DM-01M39PR43C8WYP2S7T8VYTYTZE).
 *
 * One row per line with a listed finding, in pages of 50 by keyset. The masked
 * account reference renders through `MaskedValue`, or "no account number" when
 * it is null; it is text only, never an attribute. The ERROR, WARN and INFO
 * count cells carry `data-severity-count` (the legend is on the page, T13).
 * Completeness is never claimed beyond what the API says: the sentence above
 * the table comes from `complete` and `complete_before_line`, and each row's
 * "may be incomplete" reads the row's own `complete`. The file-level counts
 * (line 0) lead the first page as a labelled row. Mounted inside its tab
 * panel, so the query runs only while the tab is shown.
 */
import { ShieldCheck } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'

import { EmptyState } from '@/components/trellis/EmptyState'
import { MaskedValue } from '@/components/trellis/MaskedValue'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableSkeletonRows } from '@/components/ui/table'
import type { ByRecordFilters, SeverityCounts } from '@/lib/api/findings'
import { Mono, count } from '@/lib/format'
import { PROVISIONAL_COPY, copyText } from '@/lib/provisional-copy'
import { byRecordQuery } from '@/lib/queries'
import { SEVERITY_ORDER } from '@/lib/severity'

import { Pagination } from './ByRuleTable'
import { ReadError } from './ReadError'
import { useCursorStack } from './useCursorStack'

function Counts({ counts }: { counts: SeverityCounts }) {
  return (
    <>
      {SEVERITY_ORDER.map((sev) => (
        <TableCell key={sev} numeric data-severity-count={sev}>
          {count(counts[sev])}
        </TableCell>
      ))}
    </>
  )
}

export function ByRecordTable({ runId, ready }: { runId: string; ready: boolean }) {
  const paging = useCursorStack('by-record')
  const filters: ByRecordFilters = paging.cursor === undefined ? {} : { cursor: paging.cursor }
  const query = useQuery(byRecordQuery(runId, filters, ready))

  if (query.isError) return <ReadError error={query.error} onStartAgain={paging.reset} />

  const page = query.data
  const fileLevel = page && paging.index === 0 && SEVERITY_ORDER.some((s) => page.file_level[s] > 0) ? page.file_level : null
  if (page && page.entries.length === 0 && fileLevel === null) {
    return <EmptyState icon={ShieldCheck} title={copyText('zeroFindingsTitle')} description={copyText('zeroFindingsBody')} />
  }

  const next = page?.next_cursor
  return (
    <div className="flex flex-col gap-3">
      {page && !page.complete ? (
        <p data-completeness="" className="text-body-sm text-navy-700">
          {PROVISIONAL_COPY.byRecordCompleteness.render({ completeBeforeLine: page.complete_before_line })}
        </p>
      ) : null}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-right">Line</TableHead>
            <TableHead>Record</TableHead>
            {SEVERITY_ORDER.map((sev) => (
              <TableHead key={sev} className="text-right">
                {sev}
              </TableHead>
            ))}
            <TableHead>Rules</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {page === undefined ? (
            <TableSkeletonRows columns={6} />
          ) : (
            <>
              {fileLevel ? (
                <TableRow>
                  <TableCell numeric>0</TableCell>
                  <TableCell className="text-navy-700">{copyText('fileLevelRow')}</TableCell>
                  <Counts counts={fileLevel} />
                  <TableCell />
                </TableRow>
              ) : null}
              {page.entries.map((e) => (
                <TableRow key={e.line}>
                  <TableCell numeric>{count(e.line)}</TableCell>
                  <TableCell>
                    {e.record_ref === null ? (
                      <span className="text-navy-600">{copyText('noAccountNumber')}</span>
                    ) : (
                      <MaskedValue value={e.record_ref} />
                    )}
                    {e.complete ? null : (
                      <span className="ml-2 text-caption text-navy-600">{copyText('mayBeIncomplete')}</span>
                    )}
                  </TableCell>
                  <Counts counts={e.counts} />
                  <TableCell className="max-w-[280px] truncate">
                    <Mono className="text-body-sm">{e.rule_ids.join(', ')}</Mono>
                  </TableCell>
                </TableRow>
              ))}
            </>
          )}
        </TableBody>
      </Table>
      <Pagination
        className="flex items-center justify-end gap-2"
        paging={{
          canPrevious: paging.index > 0,
          canNext: next !== undefined,
          onPrevious: paging.previous,
          onNext: () => next !== undefined && paging.next(next),
        }}
      />
    </div>
  )
}
